import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, ExternalLink, ChevronDown, ChevronUp, Brain, Target, FileText, Sparkles, Clock, AlertCircle, Info, Zap } from 'lucide-react';
import { toast } from 'react-hot-toast';
import StrategicSummaryService from '../../../services/StrategicSummaryService';

const AIInsightsTab = ({ profileData, aiConfidence, onRegenerate, isStudentView = true }) => {
  // Calculate response quality from assessment analysis if available
  const getResponseQuality = () => {
    if (profileData?.assessment?.response_analysis?.responseQuality) {
      return profileData.assessment.response_analysis.responseQuality;
    }
    // Fallback: use aiConfidence as a proxy for response quality
    return aiConfidence;
  };
  
  const responseQuality = getResponseQuality();
  const navigate = useNavigate();
  const [regenerating, setRegenerating] = useState(false);
  const [regeneratingExecutive, setRegeneratingExecutive] = useState(false);
  const [formattedInsights, setFormattedInsights] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    strategic_insights: true,
    strategic: true,
    executive: true,
    assessment: true
  });

  // Load formatted insights if assessment data exists
  useEffect(() => {
    // If formatted insights are already in the database, use them
    if (profileData?.assessment?.formatted_insights) {
      // Handle both old and new formats of formatted_insights
      const rawInsights = profileData.assessment.formatted_insights?.insights || profileData.assessment.formatted_insights;
      
      // Extract text content from potentially complex nested structures
      const extractContent = (field) => {
        if (!field) return '';
        if (typeof field === 'string') return field;
        // Handle nested object structures from FormattingCustomizer
        if (field.content) return field.content;
        if (field.description) return field.description;
        if (field.text) return field.text;
        if (field.summary) return field.summary;
        // Handle arrays
        if (Array.isArray(field)) {
          return field.map(item => typeof item === 'string' ? item : item.content || item.text || '').join(' ');
        }
        // Fallback: try to extract any string content
        return '';
      };
      
      const formattedData = {
        strategicEdge: extractContent(
          rawInsights.strategicEdge || 
          rawInsights.strategicPositioning || 
          rawInsights.tacticalAdvantages
        ),
        intellectualSignature: extractContent(
          rawInsights.intellectualSignature || 
          rawInsights.intellectualProfile || 
          rawInsights.cognitiveProfile
        ),
        hiddenSuperpowers: extractContent(
          rawInsights.hiddenSuperpowers || 
          rawInsights.superpowers || 
          rawInsights.uniqueStrengths
        )
      };
      
      // Only set if we have at least one non-empty field
      if (formattedData.strategicEdge || formattedData.intellectualSignature || formattedData.hiddenSuperpowers) {
        setFormattedInsights(formattedData);
      }
    }
    // Otherwise, if raw insights exist but formatted don't, parse them
    else if (profileData?.assessment?.insights && !loadingInsights) {
      loadFormattedInsights();
    }
  }, [profileData?.assessment?.insights, profileData?.assessment?.formatted_insights]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const loadFormattedInsights = async () => {
    if (!profileData?.assessment?.insights || loadingInsights) return;
    
    try {
      setLoadingInsights(true);
      const insights = await StrategicSummaryService.parseAssessmentInsights(
        profileData.assessment.insights,
        profileData.academic?.user_id || profileData.assessment?.user_id
      );
      setFormattedInsights(insights);
    } catch (error) {
      console.error('Error loading formatted insights:', error);
      // Fallback to regex parsing if API fails
      setFormattedInsights(parseInsights(profileData.assessment.insights));
    } finally {
      setLoadingInsights(false);
    }
  };

  const handleParseInsights = async () => {
    if (!profileData?.assessment?.insights) {
      toast.error('No assessment insights to parse');
      return;
    }
    
    try {
      setLoadingInsights(true);
      const insights = await StrategicSummaryService.parseAssessmentInsights(
        profileData.assessment.insights,
        profileData.academic?.user_id || profileData.assessment?.user_id
      );
      setFormattedInsights(insights);
      toast.success('Assessment insights parsed successfully');
    } catch (error) {
      console.error('Error parsing insights:', error);
      toast.error('Failed to parse assessment insights');
    } finally {
      setLoadingInsights(false);
    }
  };

  const handleRegenerateStrategic = async () => {
    try {
      setRegenerating(true);
      // Call static method directly on the class, not on an instance
      await StrategicSummaryService.generateStrategicSummary(profileData.academic.user_id);
      toast.success('Strategic summary regenerated successfully');
      onRegenerate();
    } catch (error) {
      console.error('Error regenerating strategic summary:', error);
      toast.error('Failed to regenerate strategic summary');
    } finally {
      setRegenerating(false);
    }
  };

  const handleRegenerateExecutive = async () => {
    if (!profileData?.academic?.ai_strategic_summary) {
      toast.error('Strategic summary must be generated first');
      return;
    }
    
    try {
      setRegeneratingExecutive(true);
      const executiveSummary = await StrategicSummaryService.generateExecutiveSummary(
        profileData.academic.ai_strategic_summary,
        profileData.academic.user_id
      );
      
      // Update local state
      profileData.academic.ai_executive_summary = executiveSummary;
      profileData.academic.ai_executive_summary_updated_at = new Date().toISOString();
      
      toast.success('Executive summary generated successfully');
      onRegenerate(); // Refresh parent data
    } catch (error) {
      console.error('Error generating executive summary:', error);
      toast.error('Failed to generate executive summary');
    } finally {
      setRegeneratingExecutive(false);
    }
  };

  const getConfidenceBadgeClass = (confidence) => {
    if (confidence >= 80) return 'bg-emerald-100 text-emerald-700';
    if (confidence >= 60) return 'bg-amber-100 text-amber-700';
    return 'bg-red-100 text-red-700';
  };

  const getConfidenceLabel = (confidence) => {
    if (confidence >= 80) return 'High Confidence';
    if (confidence >= 60) return 'Medium Confidence';
    return 'Low Confidence';
  };

  const formatLastUpdated = (date) => {
    if (!date) return 'Never generated';
    
    const daysSince = Math.floor((new Date() - new Date(date)) / (1000 * 60 * 60 * 24));
    if (daysSince === 0) return 'Generated today';
    if (daysSince === 1) return 'Generated yesterday';
    if (daysSince < 7) return `Generated ${daysSince} days ago`;
    if (daysSince < 30) return `Generated ${Math.floor(daysSince / 7)} weeks ago`;
    return `Generated ${Math.floor(daysSince / 30)} months ago`;
  };

  const parseInsights = (insights) => {
    // Handle both string and object formats for insights
    if (!insights) return null;
    
    if (typeof insights === 'string') {
      try {
        // First try to parse as JSON
        const parsed = JSON.parse(insights);
        if (typeof parsed === 'object') {
          return parsed;
        }
      } catch {
        // If not JSON, extract key sections from the strategic assessment text
        const extractedInsights = {};
        
        // Look for common patterns in the assessment insights text
        // Pattern 1: "YOUR STRATEGIC ADVANTAGE" section
        if (insights.includes('STRATEGIC ADVANTAGE')) {
          const match = insights.match(/STRATEGIC ADVANTAGE[:\s]*([^#\n]*(?:\n(?![#\n])[^#\n]*)*)/i);
          if (match) {
            extractedInsights.strategicAdvantage = match[1].trim();
          }
        }
        
        // Pattern 2: "HOW YOUR BRAIN WORKS" or "Intellectual Signature"
        if (insights.includes('HOW YOUR BRAIN WORKS') || insights.includes('Intellectual Signature')) {
          const match = insights.match(/(?:HOW YOUR BRAIN WORKS|Intellectual Signature)[:\s]*([^#\n]*(?:\n(?![#\n])[^#\n]*)*)/i);
          if (match) {
            extractedInsights.intellectualSignature = match[1].trim();
          }
        }
        
        // Pattern 3: "SECRET WEAPONS" or "Hidden Superpowers"
        if (insights.includes('SECRET WEAPONS') || insights.includes('Hidden Superpowers')) {
          const match = insights.match(/(?:SECRET WEAPONS|Hidden Superpowers)[:\s]*([^#\n]*(?:\n(?![#\n])[^#\n]*)*)/i);
          if (match) {
            extractedInsights.superpowers = match[1].trim();
          }
        }
        
        // Pattern 4: Extract first meaningful paragraph if no structured sections found
        if (Object.keys(extractedInsights).length === 0) {
          // Remove markdown headers and get first substantial paragraph
          const cleanText = insights
            .replace(/^#+\s+.+$/gm, '') // Remove markdown headers
            .replace(/\*\*/g, '') // Remove bold markers
            .trim();
          
          const paragraphs = cleanText.split(/\n\n+/).filter(p => p.length > 50);
          if (paragraphs.length > 0) {
            extractedInsights.summary = paragraphs[0].substring(0, 500) + (paragraphs[0].length > 500 ? '...' : '');
          }
        }
        
        return extractedInsights;
      }
    }
    
    return insights;
  };

  return (
    <div className="flex flex-row gap-4">
    <div className="space-y-4 w-2/3">
      {/* Strategic Profile Insights - Moved from AssessmentDepthAnalyzer */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden transition-all duration-200 hover:border-indigo-500 hover:shadow-lg">
        
        {/* Essay Generation Call-to-Action with Quality Threshold */}
        <div className="bg-gradient-to-r from-indigo-50 to-violet-50 border border-indigo-200 rounded-lg p-5 text-center">                 
                  <h4 className="font-semibold text-indigo-900 mb-2">Transform Insights into Essays</h4>
                  <p className="text-indigo-800 text-sm mb-4">
                    Your strategic insights reveal unique stories and competitive advantages that can become compelling college application essays.
                  </p>
                  
                  {responseQuality > 70 ? (
                    <button 
                      onClick={() => navigate('/college/essays?tab=ideas&mode=insights&source=strategic-insights')}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium text-sm transition-colors"
                    >
                      Generate Strategic Essay Topics
                    </button>
                  ) : (
                    <div className="space-y-3">
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                        <div className="text-amber-800 font-medium text-sm mb-1">
                          Generate Additional Essay Topics
                        </div>
                        <div className="text-amber-700 text-xs">
                        When we get your responses to around 70% detailed, I can start suggesting more nuanced essay topics based on what you've actually done.
                        </div>
                        <div className="mt-2 text-amber-600 text-xs">
                          Current: {responseQuality}% • Target: 70%+
                        </div>
                      </div>
                      <button 
                        onClick={() => navigate('/college/profile-hub')}
                        className="bg-violet-600 hover:bg-violet-700 text-white px-4 py-2 rounded-lg font-medium text-sm transition-colors"
                      >
                        Improve Response Quality
                      </button>
                    </div>
                  )}
                </div>
      </div>
      {/* Strategic Summary */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden transition-all duration-200 hover:border-indigo-500 hover:shadow-lg">
        <div className="px-5 py-4 bg-slate-900 text-white flex justify-between items-center">
          <div className="flex items-center gap-3 flex-1">
            <button
              onClick={() => toggleSection('strategic')}
              className="flex items-center gap-2 flex-1"
            >
              <Brain className="w-5 h-5" />
              <div className="flex-1">
                <h3 className="text-base font-semibold mb-1">Strategic Summary</h3>
                <div className="text-sm opacity-80 flex items-center gap-3 flex-wrap">
                  <span>
                    {profileData?.academic?.ai_strategic_summary ? 
                      `${profileData.academic.ai_strategic_summary.length} characters` : 
                      'Not generated'}
                  </span>
                  <div className={`px-2 py-0.5 rounded text-xs font-medium ${getConfidenceBadgeClass(aiConfidence)}`}>
                    {getConfidenceLabel(aiConfidence)}
                  </div>
                  <span className="text-xs opacity-70">
                    {formatLastUpdated(profileData?.academic?.ai_strategic_summary_updated_at)}
                  </span>
                </div>
              </div>
              {expandedSections.strategic ? (
                <ChevronUp className="w-5 h-5 opacity-60" />
              ) : (
                <ChevronDown className="w-5 h-5 opacity-60" />
              )}
            </button>
          </div>
          <div className="flex items-center gap-2 ml-4">
            <button
              className="bg-white bg-opacity-20 text-white border border-white border-opacity-30 px-3 py-1.5 rounded-md text-sm cursor-pointer transition-all duration-200 hover:bg-opacity-30 disabled:opacity-50"
              onClick={handleRegenerateStrategic}
              disabled={regenerating}
            >
              {regenerating ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                'Regenerate'
              )}
            </button>
          </div>
        </div>
        
        {expandedSections.strategic && (
          <div className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-sm text-slate-600">Confidence Score:</span>
              <div className="w-24 h-2 bg-stone-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-teal-500 transition-all duration-300"
                  style={{ width: `${aiConfidence}%` }}
                />
              </div>
              <span className="text-sm font-semibold">{aiConfidence}%</span>
            </div>
            
            {profileData?.academic?.ai_strategic_summary ? (
              <div className="space-y-4">
                {/* Check if profile needs updating */}
                {(() => {
                  const lastUpdated = profileData?.academic?.ai_strategic_summary_updated_at;
                  const daysSinceUpdate = lastUpdated ? 
                    Math.floor((new Date() - new Date(lastUpdated)) / (1000 * 60 * 60 * 24)) : null;
                  const needsUpdate = daysSinceUpdate && daysSinceUpdate > 30;
                  
                  if (isStudentView && needsUpdate) {
                    return (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3 mb-4">
                        <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-amber-800 font-medium mb-1">Your profile insights are {daysSinceUpdate} days old</p>
                          <p className="text-amber-700 text-sm mb-3">Update your profile to get fresh insights based on your latest achievements and experiences.</p>
                          <button
                            onClick={handleRegenerateStrategic}
                            className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-sm font-medium"
                          >
                            Update My Profile
                          </button>
                        </div>
                      </div>
                    );
                  }
                })()}
                
                {/* Parse and render markdown sections */}
                {(() => {
                  const summary = profileData.academic.ai_strategic_summary;
                  const sections = [];
                  
                  // Split by ### headers
                  const sectionMatches = summary.split(/###\s+([^\n]+)\n/);
                  
                  for (let i = 1; i < sectionMatches.length; i += 2) {
                    const title = sectionMatches[i];
                    const content = sectionMatches[i + 1] || '';
                    
                    // Filter sections based on student view
                    const shouldSkip = isStudentView && (
                      title.includes('Blind Spots') || 
                      title.includes('Concerns') ||
                      title.includes('Weaknesses') ||
                      title.includes('Challenges') ||
                      (title.includes('BEHAVIORAL') && !title.includes('Strengths'))
                    );
                    
                    if (shouldSkip) continue;
                    
                    // Reframe titles for student view
                    let displayTitle = title;
                    let sectionIcon = '🎯';
                    let bgColor = 'bg-azure-50';
                    let borderColor = 'border-azure-200';
                    let titleColor = 'text-azure-900';
                    
                    if (isStudentView) {
                      // Student-friendly titles and icons
                      if (title.includes('IDENTITY')) {
                        displayTitle = 'YOUR STRATEGIC ADVANTAGE';
                        sectionIcon = '🎯';
                        bgColor = 'bg-gradient-to-br from-violet-50 to-pink-50';
                        borderColor = 'border-violet-200';
                        titleColor = 'text-violet-900';
                      } else if (title.includes('BEHAVIORAL')) {
                        displayTitle = 'HOW YOUR MIND WORKS';
                        sectionIcon = '🧠';
                        bgColor = 'bg-gradient-to-br from-azure-50 to-cyan-50';
                        borderColor = 'border-azure-200';
                        titleColor = 'text-azure-900';
                      } else if (title.includes('FIT')) {
                        displayTitle = 'COLLEGE FIT INSIGHTS';
                        sectionIcon = '🏫';
                        bgColor = 'bg-gradient-to-br from-emerald-50 to-emerald-50';
                        borderColor = 'border-emerald-200';
                        titleColor = 'text-emerald-900';
                      } else if (title.includes('APPLICATIONS')) {
                        displayTitle = 'STRATEGIC OPPORTUNITIES';
                        sectionIcon = '💡';
                        bgColor = 'bg-gradient-to-br from-amber-50 to-yellow-50';
                        borderColor = 'border-amber-200';
                        titleColor = 'text-amber-900';
                      }
                    } else {
                      // Backend/parent view - keep original analytical titles
                      if (title.includes('IDENTITY')) {
                        sectionIcon = '🎯';
                        bgColor = 'bg-violet-50';
                        borderColor = 'border-violet-200';
                        titleColor = 'text-violet-900';
                      } else if (title.includes('BEHAVIORAL')) {
                        sectionIcon = '🧠';
                        bgColor = 'bg-azure-50';
                        borderColor = 'border-azure-200';
                        titleColor = 'text-azure-900';
                      } else if (title.includes('FIT')) {
                        sectionIcon = '🎓';
                        bgColor = 'bg-emerald-50';
                        borderColor = 'border-emerald-200';
                        titleColor = 'text-emerald-900';
                      } else if (title.includes('APPLICATIONS')) {
                        sectionIcon = '📝';
                        bgColor = 'bg-amber-50';
                        borderColor = 'border-amber-200';
                        titleColor = 'text-amber-900';
                      }
                    }
                    
                    // Parse content and filter sensitive items for student view
                    const contentItems = [];
                    content.split(/\*\*([^:]+):\*\*/).forEach((part, idx) => {
                      if (idx === 0 && !part.trim()) return;
                      if (idx % 2 === 1) {
                        const label = part;
                        // Skip sensitive labels in student view
                        if (isStudentView && (
                          label.includes('Blind Spot') ||
                          label.includes('Weakness') ||
                          label.includes('Concern') ||
                          label.includes('Risk')
                        )) {
                          return;
                        }
                        contentItems.push({ type: 'label', text: label });
                      } else {
                        const cleanContent = part.trim()
                          .replace(/\[([^\]]+)\]/g, '$1')
                          .split('\n')[0];
                        if (cleanContent) {
                          contentItems.push({ type: 'content', text: cleanContent });
                        }
                      }
                    });
                    
                    sections.push(
                      <div key={title} className={`${bgColor} border ${borderColor} rounded-lg p-5 transition-all hover:shadow-md`}>
                        <h4 className={`font-bold ${titleColor} mb-4 flex items-center gap-2 text-lg`}>
                          <span className="text-2xl">{sectionIcon}</span>
                          <span>{displayTitle}</span>
                        </h4>
                        <div className="space-y-3">
                          {contentItems.map((item, idx) => {
                            if (item.type === 'label') {
                              return (
                                <div key={idx} className="mt-3 first:mt-0">
                                  <span className="font-semibold text-slate-800">{item.text}:</span>
                                </div>
                              );
                            } else {
                              return (
                                <div key={idx} className="text-slate-700 ml-4 text-sm leading-relaxed">
                                  {item.text}
                                </div>
                              );
                            }
                          })}
                        </div>
                      </div>
                    );
                  }
                  
                  // Add call-to-action for students
                  if (isStudentView && sections.length > 0) {
                    sections.push(
                      <div key="cta" className="bg-gradient-to-r from-indigo-50 to-violet-50 border border-indigo-200 rounded-lg p-5 text-center">
                        <Sparkles className="w-8 h-8 text-indigo-600 mx-auto mb-3" />
                        <p className="text-indigo-900 font-medium mb-2">Want to see how this applies to specific schools?</p>
                        <button 
                          onClick={() => navigate('/college/colleges')}
                          className="text-indigo-600 hover:text-indigo-700 font-medium text-sm underline"
                        >
                          Check the strategy notes for each of your selected colleges →
                        </button>
                      </div>
                    );
                  }
                  
                  return sections.length > 0 ? sections : (
                    // Fallback to simple formatted display if parsing fails
                    <div className="bg-stone-50 border border-stone-200 rounded-lg p-5">
                      <div className="whitespace-pre-wrap font-sans text-slate-700 text-sm leading-relaxed">
                        {summary}
                      </div>
                    </div>
                  );
                })()}
                
                <div className="text-right mt-4 text-xs text-stone-500 bg-stone-50 rounded px-3 py-2">
                  <div className="flex justify-between items-center">
                    <div className="flex gap-4">
                      <span>📊 {profileData.academic.ai_strategic_summary.split(' ').length} words</span>
                      <span>📝 {profileData.academic.ai_strategic_summary.split('\n').length} lines</span>
                    </div>
                    <div>
                      Last updated: {formatLastUpdated(profileData.academic.ai_strategic_summary_updated_at)}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-5 text-center">
                <p className="text-amber-800 mb-3">No strategic summary generated yet</p>
                <button
                  className="px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors"
                  onClick={handleRegenerateStrategic}
                  disabled={regenerating}
                >
                  Generate Strategic Summary
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Executive Summary */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden transition-all duration-200 hover:border-indigo-500 hover:shadow-lg">
        <div className="px-5 py-4 bg-slate-900 text-white flex justify-between items-center">
          <div className="flex items-center gap-3 flex-1">
            <button
              onClick={() => toggleSection('executive')}
              className="flex items-center gap-2 flex-1"
            >
              <FileText className="w-5 h-5" />
              <div className="flex-1">
                <h3 className="text-base font-semibold mb-1">Executive Summary</h3>
                <div className="text-sm opacity-80 flex items-center gap-3 flex-wrap">
                  <span>150 words</span>
                  <div className={`px-2 py-0.5 rounded text-xs font-medium ${getConfidenceBadgeClass(aiConfidence)}`}>
                    {getConfidenceLabel(aiConfidence)}
                  </div>
                  <span className="text-xs opacity-70">
                    {formatLastUpdated(profileData?.academic?.ai_executive_summary_updated_at)}
                  </span>
                </div>
              </div>
              {expandedSections.executive ? (
                <ChevronUp className="w-5 h-5 opacity-60" />
              ) : (
                <ChevronDown className="w-5 h-5 opacity-60" />
              )}
            </button>
          </div>
          <div className="flex items-center gap-2 ml-4">
            <button
              className="bg-white bg-opacity-20 text-white border border-white border-opacity-30 px-3 py-1.5 rounded-md text-sm cursor-pointer transition-all duration-200 hover:bg-opacity-30 disabled:opacity-50"
              onClick={handleRegenerateExecutive}
              disabled={regeneratingExecutive || !profileData?.academic?.ai_strategic_summary}
              title={!profileData?.academic?.ai_strategic_summary ? "Generate Strategic Summary first" : "Generate Executive Summary"}
            >
              {regeneratingExecutive ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                profileData?.academic?.ai_executive_summary ? 'Regenerate' : 'Generate'
              )}
            </button>
          </div>
        </div>
        
        {expandedSections.executive && (
          <div className="p-5">
            {profileData?.academic?.ai_executive_summary ? (
              <div className="space-y-4">
                {/* Display the executive summary as a clean, formatted paragraph */}
                <div className="bg-gradient-to-br from-indigo-50 to-violet-50 rounded-lg p-5 border border-indigo-200">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="p-2 bg-indigo-100 rounded-lg">
                      <Sparkles className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-indigo-900 mb-2">Your Elevator Pitch</h4>
                      <p className="text-slate-800 leading-relaxed whitespace-pre-wrap">
                        {profileData.academic.ai_executive_summary}
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 pt-3 border-t border-indigo-200">
                    <p className="text-xs text-indigo-700 flex items-center gap-2">
                      <Info className="w-3 h-3" />
                      <span>Memorize this 150-word summary for interviews and applications</span>
                    </p>
                  </div>
                </div>
                
                {/* Word count indicator */}
                <div className="flex justify-end">
                  <span className="text-xs text-stone-500">
                    Word count: {profileData.academic.ai_executive_summary.split(' ').length}
                  </span>
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-slate-600">Executive summary will be generated with the strategic summary</p>
              </div>
            )}
          </div>
        )}
      </div>
</div>
<div className="w-1/3">
      {/* Assessment Insights */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden transition-all duration-200 hover:border-indigo-500 hover:shadow-lg">
        <div className="px-5 py-4 bg-slate-900 text-white justify-between items-center">
          <div className="flex items-center gap-3 flex-1">
            <button
              onClick={() => toggleSection('assessment')}
              className="flex items-center gap-2 flex-1 flex-col"
            >
              <div className="flex flex-row items-center gap-2">
              <div className="flex flex-row items-center gap-2"><Target className="w-5 h-5" />
          
                <span className="text-base font-semibold mb-1">Assessment Results</span></div>
                {expandedSections.assessment ? (
                <ChevronUp className="w-5 h-5 opacity-60" />
              ) : (
                <ChevronDown className="w-5 h-5 opacity-60" />
              )}
              </div>
                <div className="text-sm opacity-80 flex items-center gap-3 flex-wrap">
                  <span>
                    {profileData?.assessment?.responses ? 'Completed' : 'Not completed'}
                  </span>
                  {profileData?.assessment?.responses && (
                    <div className="px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700">
                      High Confidence
                    </div>
                  )}
                 
                </div>
              
            </button>
          </div>
          {profileData?.assessment?.results_permalink && (
            <div className="flex items-center gap-2 mt-2">
              <button
                className="bg-white bg-opacity-20 text-white border border-white border-opacity-30 px-3 py-1.5 rounded-md text-sm cursor-pointer transition-all duration-200 hover:bg-opacity-30"
                onClick={() => navigate(`/college/strategic-profile/${profileData.assessment.results_permalink}`)}
              >
                View Full Results
              </button>
              <button
              onClick={handleParseInsights}
              className="bg-white bg-opacity-20 text-white border border-white border-opacity-30 px-3 py-1.5 rounded-md text-sm cursor-pointer transition-all duration-200 hover:bg-opacity-30"
              disabled={loadingInsights}
            >
              {loadingInsights ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                'Retake Assessment'
              )}
            </button>
            </div>
            
          )}
           <div className="text-xs opacity-70 text-right mt-2">
                    {formatLastUpdated(profileData?.assessment?.created_at)}
                  </div>
        </div>
        
        {expandedSections.assessment && (
          <div className="p-5">
            {profileData?.assessment?.responses ? (
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-slate-100">
                  <span className="font-medium text-slate-600 text-sm">Assessment Completion</span>
                  <span className="text-slate-800 font-semibold text-sm">100% - Complete</span>
                </div>
                
                {/* Display formatted insights or loading state */}
                {loadingInsights ? (
                  <div className="bg-stone-50 border border-stone-200 rounded-lg p-4">
                    <div className="flex items-center justify-center gap-3 py-8">
                      <RefreshCw className="w-5 h-5 text-stone-400 animate-spin" />
                      <span className="text-slate-600">Loading insights...</span>
                    </div>
                  </div>
                ) : formattedInsights ? (
                  <div className="space-y-4">
                    {/* Strategic Edge */}
                    {formattedInsights.strategicEdge && (
                      <div className="bg-gradient-to-br from-violet-50 to-pink-50 border border-violet-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <div className="p-2 bg-violet-100 rounded-lg">
                            <Target className="w-5 h-5 text-violet-600" />
                          </div>
                          <div className="flex-1">
                            <h5 className="font-semibold text-violet-900 mb-2">Your Strategic Edge</h5>
                            <p className="text-slate-800 leading-relaxed">
                              {String(formattedInsights.strategicEdge || '')}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Intellectual Signature */}
                    {formattedInsights.intellectualSignature && (
                      <div className="bg-gradient-to-br from-azure-50 to-cyan-50 border border-azure-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <div className="p-2 bg-azure-100 rounded-lg">
                            <Brain className="w-5 h-5 text-azure-600" />
                          </div>
                          <div className="flex-1">
                            <h5 className="font-semibold text-azure-900 mb-2">How Your Mind Works</h5>
                            <p className="text-slate-800 leading-relaxed">
                              {String(formattedInsights.intellectualSignature || '')}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Hidden Superpowers */}
                    {formattedInsights.hiddenSuperpowers && (
                      <div className="bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <div className="p-2 bg-amber-100 rounded-lg">
                            <Zap className="w-5 h-5 text-amber-600" />
                          </div>
                          <div className="flex-1">
                            <h5 className="font-semibold text-amber-900 mb-2">Your Hidden Superpowers</h5>
                            <p className="text-slate-800 leading-relaxed">
                              {String(formattedInsights.hiddenSuperpowers || '')}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : profileData.assessment.insights ? (
                  <div className="bg-stone-50 border border-stone-200 rounded-lg p-4">
                    <h4 className="font-semibold text-slate-800 mb-3">Key Insights from Strategic Assessment:</h4>
                    <p className="text-sm text-slate-600 italic">Click "Parse Insights" to format your assessment results</p>
                    <button
                      onClick={handleParseInsights}
                      className="mt-3 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
                      disabled={loadingInsights}
                    >
                      Parse Insights
                    </button>
                  </div>
                ) : (
                  <div className="bg-stone-50 border border-stone-200 rounded-lg p-4">
                    <p className="text-sm text-slate-600 italic">No insights available</p> 
                  </div>
                )}
                
                {profileData.assessment.results_permalink && (
                  <div className="flex justify-between items-center py-2 border-b border-slate-100">
                    <span className="font-medium text-slate-600 text-sm">Results Permalink</span>
                    <a
                      href={`/college/strategic-profile/${profileData.assessment.results_permalink}`}
                      className="text-indigo-600 hover:text-indigo-700 text-sm flex items-center gap-1"
                    >
                      View Results
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-6">
                <Target className="w-12 h-12 text-stone-400 mx-auto mb-3" />
                <p className="text-slate-600 mb-3">No assessment completed yet</p>
                <button
                  className="px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors"
                  onClick={() => navigate('/college/assessment')}
                >
                  Take Strategic Assessment
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
</div>
  );
};

export default AIInsightsTab;